import { RadioModel } from '@modules/entertainment/models/radio.model.ts';
import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import ViewControls from '@components/view-controls/view-controls.tsx';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import { radioColumns } from './radio-table-component/columns/columns.tsx';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@shared/components/icons/icon.tsx';
import RadioDialogForm from '../radio-dialog/radio-dialog-forms.tsx';
import useRadioStore from '../../store/radio.store.ts';
import { RadioRowDataProps } from '@modules/entertainment/models/radio.model.ts';
import { getRadio } from '@shared/api/entertainment-api/radio-api.ts';
import { TriggerTypeEnum } from '@shared/models/table.model.ts';
import { useSearchParams } from 'react-router-dom';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';
import useTableSelection from '@hooks/use-table-selection.ts';
import { getRowIdKey } from '@utils/table-selection.utils.tsx';

const RadioTable: React.FC = () => {
  const { deleteRow, editRow, createRow, genreList, isLoading, getGenreList } =
    useRadioStore();
  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const {
    selectedKeys: selectedRows,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow: handleSelectRow,
    togglePage: handleSelectAll,
    clearSelection
  } = useTableSelection<RadioModel>({
    tableStore,
    getRowKey: getRowIdKey
  });
  const [language, setLanguage] = useState(LanguageEnum.English);
  const [view, setView] = useState(ViewControlsEnum.List);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<RadioRowDataProps | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const count = selectedRows.length;

  const handleEdit = (row: any) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItem(row);
  };

  const handleDeleteConfirm = () => {
    deleteRow({
      id: selectedItem?._id ? selectedItem?._id : selectedRows,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleFormSave = (data: any) => {
    if (selectedItem?._id) {
      editRow({
        data,
        id: selectedItem?._id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createRow({
        data,
        state: tableStore.getState(),
        handleClose
      });
    }
  };

  const handleClose = () => {
    resetAllSelectedMedia();
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setSelectedItem(null);
    clearSelection();
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete(selectedRows);
        break;
      case ViewControlsEnum.ImportPlaylist:
        break;
      case ViewControlsEnum.SelectAll:
        break;
      case ViewControlsEnum.Search:
        break;
      case ViewControlsEnum.Language:
        setLanguage(action.value as LanguageEnum);
        break;
    }
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const columns = radioColumns(
    handleEdit,
    handleDelete,
    {
      handleSelectRow,
      handleSelectAll,
      isPageAllSelected,
      isPagePartiallySelected,
      isRowSelected
    },
    genreList
  );

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      await getGenreList();

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        setOpenDialog(true);
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls
          onAction={handleAction}
          selectedRows={selectedRows}
          showLanguageSelect={false}
        />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => handleEdit(selectedItem)}
        >
          {t('add')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getRadio}
            columns={columns}
            language={language}
            onTriggerAction={handleTableTrigger}
            tableKey="entertainment-radio-table"
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          title={t('entertainment_add_radio_title')}
          content={
            <RadioDialogForm
              selectedItem={selectedItem}
              onSave={handleFormSave}
              genreList={genreList}
            />
          }
          onClose={handleClose}
        />

        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          isLoading={isLoading()}
          title={
            selectedItem?._id
              ? t('delete_popup_title')
              : t('delete_popup_title_many_items', { count })
          }
        />
      </div>
    </div>
  );
};

export default RadioTable;
